/**
 * Single source of truth for "after auth, where does this user belong?"
 *
 * Used by Login (post sign-in redirect) and ProtectedRoute (default
 * redirect target when an authorized route doesn't match the user's
 * permission set). Keeping these in lockstep prevents the redirect
 * bounce-loops we used to see when Login sent everyone to /me but
 * ProtectedRoute then bounced them to /dashboard or /portal.
 *
 * Order matters:
 *   1. Email not verified              → /verify-email
 *   2. Platform staff (super_admins)   → /superadmin
 *   3. Brand portal users              → /portal
 *   4. Creator portal users            → /creator
 *   5. Agency user without workspace   → /onboarding (bootstrap_agency)
 *   6. Agency user mid-onboarding      → /onboarding
 *   7. Everyone else                   → /dashboard
 */

import type { User } from '@supabase/supabase-js';
import type { ApprovalStatus, AgencyRole } from '@/contexts/AgencyContext';

export type AppRole = 'super_admin' | 'agency_staff' | 'brand_client' | 'creator' | null;

export interface LandingContext {
  user: User | null;
  role: AppRole;
  isSuperAdmin: boolean;
  agencyId: string | null;
  agencyRole: AgencyRole;
  onboardingComplete: boolean;
  /** Retained for backward-compat with callers; no longer gates routing. */
  approvalStatus: ApprovalStatus | null;
}

export function resolveLandingRoute(ctx: LandingContext): string {
  if (!ctx.user) return '/login';
  if (!ctx.user.email_confirmed_at) return '/verify-email';
  if (ctx.isSuperAdmin) return '/superadmin';

  if (ctx.role === 'brand_client') return '/portal';
  if (ctx.role === 'creator') return '/creator';

  // Agency users go straight in. No approval queue.
  if (!ctx.agencyId || !ctx.onboardingComplete) return '/onboarding';

  return '/dashboard';
}
